import * as React from 'react';
import {
  addWeeks,
  differenceInWeeks,
  eachDayOfInterval,
  endOfDay,
  endOfWeek,
  format,
  getDay,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
  startOfWeek,
  subWeeks,
} from 'date-fns';
import { Virtual } from 'swiper';
import 'swiper/css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { lessonShortName } from 'constants/sports';
import { DAY_INDEX_SHORT } from 'constants/time';
import {
  CoachAvailability,
  CoachAvailabilityStatusesEnum,
  LessonTypesEnum,
} from 'types/generated/client';
import { GetCoachBookingPageQuery as ClientGetCoachBookingPageQuery } from 'types/generated/client';
import getFirstNameLastInitial from 'utils/shared/string/getFirstNameLastInitial';
import { useCalendarAccounts } from 'hooks/useCalendarAccounts';
import { useCalendarEvents } from 'hooks/useCalendarEvents';
import GoogleCalendarLogo from 'svg/GoogleCalendarLogo';
import LoadSpinner from 'svg/LoadSpinner';
import classNames from 'styles/utils/classNames';
import { CalendarSwiperProps, CalendarWeekProps } from './props';
import { ExternalEventBlock, LessonBlock, TimeLine, WeeklyCalendarGrid } from './styles';
import { useGroupEventsByStartDate } from './useGroupEventsByStartDate';

const MINUTES_IN_HOUR = 60;
const DAYS_PER_WEEK = 7;
const WEEKS_FOR_CALENDAR = 53;
const WEEKS_FOR_FETCHING = 5;
const CURRENT_DATE_INDEX = Math.floor((WEEKS_FOR_CALENDAR * 2) / 2) - 1;
// NOTE: Choosing 6 because part of the
const INDEX_OF_EARLIEST_START_TIME = 8;
// NOTE: This should be an object or use 24hr scale and convert
const TIMES = [
  { time: 0, displayTime: '12', ampm: 'am' },
  { time: 1, displayTime: '1', ampm: 'am' },
  { time: 2, displayTime: '2', ampm: 'am' },
  { time: 3, displayTime: '3', ampm: 'am' },
  { time: 4, displayTime: '4', ampm: 'am' },
  { time: 5, displayTime: '5', ampm: 'am' },
  { time: 6, displayTime: '6', ampm: 'am' },
  { time: 7, displayTime: '7', ampm: 'am' },
  { time: 8, displayTime: '8', ampm: 'am' },
  { time: 9, displayTime: '9', ampm: 'am' },
  { time: 10, displayTime: '10', ampm: 'am' },
  { time: 11, displayTime: '11', ampm: 'am' },
  { time: 12, displayTime: '12', ampm: 'pm' },
  { time: 13, displayTime: '1', ampm: 'pm' },
  { time: 14, displayTime: '2', ampm: 'pm' },
  { time: 15, displayTime: '3', ampm: 'pm' },
  { time: 16, displayTime: '4', ampm: 'pm' },
  { time: 17, displayTime: '5', ampm: 'pm' },
  { time: 18, displayTime: '6', ampm: 'pm' },
  { time: 19, displayTime: '7', ampm: 'pm' },
  { time: 20, displayTime: '8', ampm: 'pm' },
  { time: 21, displayTime: '9', ampm: 'pm' },
  { time: 22, displayTime: '10', ampm: 'pm' },
  { time: 23, displayTime: '11', ampm: 'pm' },
];

const CalendarWeek: React.FC<CalendarWeekProps> = ({
  index,
  slideContent,
  now,
  hours,
  minutes,
  toggleBottomSheet,
  scrollHeight,
  setScrollHeight,
  slideRefs,
  hasSetInitialScroll,
  setHasSetInitialScroll,
  activeCalendarBlock,
  setActiveCalendarBlock,
  onButtonClick,
  activeLessonId,
  setActiveLessonId,
  eventsByStartDate,
  isOwner,
  showName = false,
  disabledDates = [],
  coachAvailability,
  isAnonymous: shouldBlockTimeForAnonymousOrUser,
  onOverflowClick,
}) => {
  const timeScaleRef = React.useRef<HTMLDivElement>(null);

  const daysOfWeek = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

  const convertMinutesToHours = (availabilities: any) => {
    return availabilities.map((availability: any) => {
      const startTimeHours = Math.floor(availability.startTimeMinutes / 60);
      const endTimeHours = Math.floor(availability.endTimeMinutes / 60);

      return {
        dayOfWeek: availability.dayOfWeek,
        startTimeHours: startTimeHours,
        endTimeHours: endTimeHours,
        status: availability.status === CoachAvailabilityStatusesEnum.Active ? true : false,
      };
    });
  };

  const convertedCoachAvailability = convertMinutesToHours(coachAvailability || []);

  React.useEffect(() => {
    if (!hasSetInitialScroll && timeScaleRef?.current?.offsetTop) {
      setHasSetInitialScroll(true);
      setScrollHeight(timeScaleRef?.current?.offsetTop);
      slideRefs.current[index]?.scroll({ top: timeScaleRef?.current?.offsetTop });
    }
  }, [hasSetInitialScroll]);

  React.useEffect(() => {
    slideRefs.current[index]?.scroll({ top: scrollHeight });
  }, [scrollHeight]);

  return (
    <>
      <div className="relative bg-color-bg-lightmode-secondary px-6 pt-4 dark:bg-color-bg-darkmode-secondary">
        <WeeklyCalendarGrid className="@container">
          <div className="border-b border-r border-color-border-input-lightmode dark:border-color-border-input-darkmode">
            &nbsp;
          </div>
          {slideContent.map((day, i) => {
            return (
              <div
                key={day?.toDateString() || i}
                className={classNames(
                  'justify-center border-b border-r border-color-border-input-lightmode pb-2 pt-1.5 text-center text-xs  @xs:text-xs @lg:flex @lg:py-[6.5px] @lg:text-base @lg:leading-[19px] dark:border-color-border-input-darkmode',
                  !!now && !!day && now.toDateString() === day.toDateString()
                    ? 'bg-brand-gray-200 text-color-text-lightmode-primary dark:bg-brand-gray-700 dark:text-color-text-darkmode-primary'
                    : 'bg-transparent text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
                )}
              >
                <div>{DAY_INDEX_SHORT[i]}</div>
                <div className="hidden @lg:mr-1.5 @lg:block">, </div>
                <div>{day ? day.getDate() : ' '}</div>
              </div>
            );
          })}
        </WeeklyCalendarGrid>
      </div>

      <div
        ref={(el) => {
          slideRefs.current[index] = el;
        }}
        className="relative flex h-full w-full flex-auto flex-col overflow-y-auto bg-color-bg-lightmode-secondary px-6 pb-24 dark:bg-color-bg-darkmode-secondary"
      >
        {TIMES.map(({ time, displayTime, ampm }, timeIndex) => {
          const emptyCells = new Array(DAYS_PER_WEEK).fill('+');
          const isCurrentHour = hours === time;

          return (
            <div
              key={time}
              className="relative"
              ref={timeIndex === INDEX_OF_EARLIEST_START_TIME ? timeScaleRef || null : null}
            >
              {!!isCurrentHour && (
                <TimeLine
                  className="absolute z-30 w-full border-b border-brand-fire-500"
                  top={((minutes || 0) / 60) * 100}
                >
                  &nbsp;
                </TimeLine>
              )}
              <WeeklyCalendarGrid>
                <div className="border-b border-r border-color-border-input-lightmode pb-2 pt-2 text-center text-xs dark:border-color-border-input-darkmode lg:text-sm">
                  <div className="text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                    {displayTime}
                  </div>
                  <div className="text-brand-gray-400">{ampm}</div>
                </div>
                {emptyCells.map((_cell, dateIndex) => {
                  const key = `${dateIndex}-${timeIndex}`;
                  const dateOfCell = format(slideContent[dateIndex], 'yyyy-MM-dd');
                  const eventsOnDate = eventsByStartDate[dateOfCell]?.events || [];
                  const isActiveTimeBlock =
                    !!slideContent[dateIndex] &&
                    dateOfCell === activeCalendarBlock?.date &&
                    time === activeCalendarBlock?.time;

                  const eventsStartingHere = eventsOnDate.filter((e) => e.startHour === time);
                  const visibleHere = eventsStartingHere.filter((e) => !e.isOverflow);
                  const overflowHere = eventsStartingHere.filter((e) => e.isOverflow);

                  const currentTime = now || new Date();
                  const year = slideContent[dateIndex].getUTCFullYear();
                  const month = slideContent[dateIndex].getUTCMonth(); // Note: months are zero-indexed (0-11)
                  const day = slideContent[dateIndex].getUTCDate();
                  const dateTimeOfCellDate = new Date(year, month, day, time, 0);

                  const isDisabled = !convertedCoachAvailability?.find((coachAvailable: any) => {
                    if (shouldBlockTimeForAnonymousOrUser) {
                      // Disable if the dateOfCell is before today or if it's today but the time is in the past
                      if (isBefore(dateTimeOfCellDate, currentTime)) {
                        return false;
                      }
                    }
                    const isTimeInRange =
                      coachAvailable?.dayOfWeek === daysOfWeek[dateIndex] &&
                      time >= coachAvailable?.startTimeHours &&
                      time <= coachAvailable?.endTimeHours &&
                      coachAvailable.status;

                    return isTimeInRange;
                  });

                  return (
                    <div key={key} className="relative">
                      <button
                        type="button"
                        onClick={() => {
                          if (isOwner) {
                            toggleBottomSheet();
                            setActiveLessonId('');
                            setActiveCalendarBlock({ time, date: dateOfCell });
                          }
                          if (shouldBlockTimeForAnonymousOrUser) {
                            !isDisabled && setActiveCalendarBlock({ time, date: dateOfCell });
                          }
                          !isDisabled && onButtonClick?.({ time, date: dateOfCell });
                        }}
                        disabled={false}
                        className={classNames(
                          'relative h-full w-full rounded-none border-b border-r bg-color-bg-lightmode-primary pt-1 text-center text-xs transition-shadow duration-300 dark:bg-color-bg-darkmode-primary',
                          isActiveTimeBlock && 'z-10 border border-color-brand-primary shadow-lg',
                          !isActiveTimeBlock && !isDisabled
                            ? 'border-color-border-input-lightmode dark:border-color-border-input-darkmode'
                            : 'border-color-border-card-lightmode bg-color-bg-lightmode-tertiary dark:border-color-border-card-darkmode dark:bg-color-bg-darkmode-tertiary',
                        )}
                      >
                        {isOwner ? ' ' : ' '}
                      </button>

                      {visibleHere.map((event) => {
                        const widthPercent = 100 / event.intervalVisibleCount;
                        const leftPercent =
                          ((event.numberInInterval - 1) / event.intervalVisibleCount) * 100;
                        const topPercent = (event.startMinute / 60) * 100;
                        // Clamp to the remainder of the start-day column so
                        // multi-day events don't overflow past the calendar.
                        // Full multi-day display would require splitting the
                        // event into per-day segments — not handled here.
                        const minutesUntilEndOfDay =
                          24 * 60 - event.startHour * 60 - event.startMinute;
                        const visibleDurationMinutes = Math.min(
                          event.durationMinutes,
                          minutesUntilEndOfDay,
                        );
                        const heightPercent = (visibleDurationMinutes / 60) * 100;

                        if (event.type === 'bounce') {
                          const isActiveLessonBlock = activeLessonId === event.id;
                          return (
                            <LessonBlock
                              key={event.id}
                              type="button"
                              onClick={() => {
                                if (shouldBlockTimeForAnonymousOrUser) return;
                                setActiveLessonId(event.id);
                                setActiveCalendarBlock(null);
                              }}
                              className={classNames(
                                'absolute z-30 flex flex-auto items-center rounded-md border bg-brand-green-200 text-center text-xs text-color-text-lightmode-primary transition-shadow duration-300 lg:text-sm',
                                isActiveLessonBlock && 'border-color-brand-primary shadow-lg',
                              )}
                              heightPercent={heightPercent}
                              widthPercent={widthPercent}
                              leftPercent={leftPercent}
                              topPercent={topPercent}
                            >
                              <span className="w-full truncate">{event.lesson.title}</span>
                            </LessonBlock>
                          );
                        }

                        return (
                          <ExternalEventBlock
                            key={event.id}
                            type="button"
                            // Google events are conflict hints — taps fall through
                            // to the empty-cell action so coaches can still book
                            // (the create-lesson sheet surfaces the conflict warning).
                            onClick={() => {
                              if (isOwner) {
                                toggleBottomSheet();
                                setActiveLessonId('');
                                setActiveCalendarBlock({ time, date: dateOfCell });
                              }
                              if (shouldBlockTimeForAnonymousOrUser) {
                                !isDisabled &&
                                  setActiveCalendarBlock({ time, date: dateOfCell });
                              }
                              !isDisabled && onButtonClick?.({ time, date: dateOfCell });
                            }}
                            className="absolute z-20 flex items-center gap-1 rounded-md border border-color-border-input-lightmode bg-color-bg-lightmode-tertiary text-left text-xs text-color-text-lightmode-secondary dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-tertiary dark:text-color-text-darkmode-secondary"
                            heightPercent={heightPercent}
                            widthPercent={widthPercent}
                            leftPercent={leftPercent}
                            topPercent={topPercent}
                          >
                            <GoogleCalendarLogo className="h-3 w-3 shrink-0" />
                            <span className="typography-product-chips-filters truncate">
                              {event.title}
                            </span>
                          </ExternalEventBlock>
                        );
                      })}

                      {overflowHere.length > 0 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOverflowClick({
                              date: dateOfCell,
                              time,
                              events: eventsStartingHere,
                            });
                          }}
                          className="absolute bottom-1 right-1 z-40 rounded-full bg-color-text-lightmode-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-color-bg-lightmode-primary shadow-sm dark:bg-color-text-darkmode-primary dark:text-color-bg-darkmode-primary"
                          aria-label={`Show ${overflowHere.length} more events`}
                        >
                          +{overflowHere.length}
                        </button>
                      )}
                    </div>
                  );
                })}
              </WeeklyCalendarGrid>
            </div>
          );
        })}
      </div>
    </>
  );
};

const CalendarSwiper: React.FC<CalendarSwiperProps> = ({
  setSwiperRef,
  setMonth,
  setYear,
  toggleBottomSheet,
  activeCalendarBlock,
  setActiveCalendarBlock,
  onButtonClick,
  activeLessonId,
  setActiveLessonId,
  lessons,
  isOwner,
  showName = false,
  coachAvailability,
  isAnonymous,
  fetchExternalCalendarEvents,
  externalEvents,
  loading,
  onOverflowClick,
}) => {
  const slideRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const [now, setNow] = React.useState<null | Date>();
  const [weeks, setWeeks] = React.useState<Date[][]>([]);
  const [scrollHeight, setScrollHeight] = React.useState(0);
  const [hasSetInitialScroll, setHasSetInitialScroll] = React.useState(false);
  const hours = now?.getHours();
  const minutes = now?.getMinutes();
  const eventsByStartDate = useGroupEventsByStartDate(lessons, externalEvents);
  const { accounts } = useCalendarAccounts();

  // External event fetch window tracking - completely independent of calendar navigation
  const eventWindowRef = React.useRef({
    startDate: null as Date | null,
    endDate: null as Date | null,
    isFetching: false,
  });

  const FETCH_WINDOW_WEEKS_PAST = 5;
  const FETCH_WINDOW_WEEKS_FUTURE = 5;

  React.useEffect(() => {
    if (accounts.length === 0) return;
    if (weeks.length > 0 && !eventWindowRef.current.startDate && !eventWindowRef.current.endDate) {
      const currentWeekIndex = WEEKS_FOR_CALENDAR - 1; // Index of the current week

      if (weeks[currentWeekIndex]) {
        const currentWeekStart = weeks[currentWeekIndex][0];

        // Calculate window dates: current ± 5 weeks
        const windowStart = startOfWeek(subWeeks(currentWeekStart, FETCH_WINDOW_WEEKS_PAST));
        const windowEnd = endOfWeek(addWeeks(currentWeekStart, FETCH_WINDOW_WEEKS_FUTURE));

        eventWindowRef.current = {
          startDate: windowStart,
          endDate: windowEnd,
          isFetching: true,
        };

        fetchExternalCalendarEvents(windowStart, windowEnd).finally(() => {
          eventWindowRef.current.isFetching = false;
        });
      }
    }
  }, [weeks, fetchExternalCalendarEvents, accounts]);

  const handleWeekChange = React.useCallback(
    (activeIndex: number) => {
      if (weeks[activeIndex]) {
        const weekDates = weeks[activeIndex];
        const currentWeekStart = weekDates[0]; // First day of week

        // Update month/year display
        setMonth(currentWeekStart.getMonth());
        setYear(`${currentWeekStart.getFullYear()}`);

        // Check if current week is outside our fetch window
        if (
          eventWindowRef.current.startDate &&
          eventWindowRef.current.endDate &&
          !eventWindowRef.current.isFetching
        ) {
          const { startDate, endDate } = eventWindowRef.current;

          // Convert to midnight for proper comparison
          const startCompare = startOfDay(startDate);
          const endCompare = endOfDay(endDate);
          const currentCompare = startOfDay(currentWeekStart);

          const isBeforeWindow = isBefore(currentCompare, startCompare);
          const isAfterWindow = isAfter(currentCompare, endCompare);

          // If current week is 1 week or less from edge of window, extend the window
          const isApproachingStart =
            !isBeforeWindow && differenceInWeeks(currentCompare, startCompare) <= 1;
          const isApproachingEnd =
            !isAfterWindow && differenceInWeeks(endCompare, currentCompare) <= 1;

          // If we're outside window or approaching edge, update the window
          if (isBeforeWindow || isAfterWindow || isApproachingStart || isApproachingEnd) {
            let newStartDate = startDate;
            let newEndDate = endDate;

            // Extend in the appropriate direction(s)
            if (isBeforeWindow || isApproachingStart) {
              newStartDate = startOfWeek(subWeeks(currentWeekStart, FETCH_WINDOW_WEEKS_PAST));
            }

            if (isAfterWindow || isApproachingEnd) {
              newEndDate = endOfWeek(addWeeks(currentWeekStart, FETCH_WINDOW_WEEKS_FUTURE));
            }

            // Store updated window
            eventWindowRef.current = {
              startDate: newStartDate,
              endDate: newEndDate,
              isFetching: true,
            };

            // Fetch events for new window
            fetchExternalCalendarEvents(newStartDate, newEndDate).finally(() => {
              eventWindowRef.current.isFetching = false;
            });
          }
        }
      }
    },
    [weeks, setMonth, setYear, fetchExternalCalendarEvents],
  );

  // Initialize date and time related variables
  React.useEffect(() => {
    const dateNow = new Date();
    setNow(dateNow);

    // Refresh the current time every few minutes so the green line moves
    let interval = setInterval(() => {
      setNow(new Date());
    }, 2 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  // TODO: Do I need to do this in the click handler instead of the effect?
  React.useEffect(() => {
    let weekDates = [];
    let pastWeeksDates = [];
    let futureWeeksDates = [];
    const dateNow = new Date();
    const firstDayOfWeek = startOfWeek(dateNow);
    const firstDayOfVisibleWeek = firstDayOfWeek;
    const lastDayOfVisibleWeek = endOfWeek(firstDayOfVisibleWeek);
    const daysInCurrentWeek = eachDayOfInterval({
      start: firstDayOfVisibleWeek,
      end: lastDayOfVisibleWeek,
    });

    for (let i = 1; i < WEEKS_FOR_CALENDAR; i++) {
      const firstDayOfPastWeek = addWeeks(firstDayOfWeek, i * -1);
      const lastDayOfPastWeek = endOfWeek(firstDayOfPastWeek);
      const daysInPastWeek = eachDayOfInterval({
        start: firstDayOfPastWeek,
        end: lastDayOfPastWeek,
      });
      pastWeeksDates.push(daysInPastWeek);

      const firstDayOfFutureWeek = addWeeks(firstDayOfWeek, i);
      const lastDayOfFutureWeek = endOfWeek(firstDayOfFutureWeek);
      const daysInFutureWeek = eachDayOfInterval({
        start: firstDayOfFutureWeek,
        end: lastDayOfFutureWeek,
      });
      futureWeeksDates.push(daysInFutureWeek);
    }

    pastWeeksDates = pastWeeksDates.reverse();

    weekDates = [...pastWeeksDates, daysInCurrentWeek, ...futureWeeksDates];

    setWeeks(weekDates);
  }, []);

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden bg-color-bg-lightmode-secondary dark:bg-color-bg-darkmode-secondary">
      {loading && (
        <div className="absolute inset-0 z-[10]  flex h-full w-full items-center justify-center bg-[#00000010]">
          <LoadSpinner className="h-10 w-10  animate-spin" />
        </div>
      )}
      {weeks?.length > 0 ? (
        <Swiper
          threshold={2}
          virtual
          modules={[Virtual]}
          onSwiper={(swiper) => {
            setSwiperRef(swiper);
          }}
          centeredSlides
          slidesPerView={1}
          loop={false}
          className="flex h-full w-full flex-col"
          initialSlide={CURRENT_DATE_INDEX}
          onSliderFirstMove={(swiper) => {
            const activeRef = slideRefs.current[swiper.activeIndex];
            if (activeRef) {
              setScrollHeight(activeRef.scrollTop);
            }
          }}
          onSlideNextTransitionStart={(swiper) => {
            const activeRef = slideRefs.current[swiper.activeIndex - 1];
            if (activeRef) {
              setScrollHeight(activeRef.scrollTop);
            }
          }}
          onSlidePrevTransitionStart={(swiper) => {
            const activeRef = slideRefs.current[swiper.activeIndex + 1];
            if (activeRef) {
              setScrollHeight(activeRef.scrollTop);
            }
          }}
          onActiveIndexChange={(swiperInstance) => {
            handleWeekChange(swiperInstance.activeIndex);
          }}
        >
          {weeks.map((slideContent, index) => (
            <SwiperSlide key={index} virtualIndex={index} className="swiper-slide h-full w-full">
              <CalendarWeek
                index={index}
                scrollHeight={scrollHeight}
                setScrollHeight={setScrollHeight}
                slideContent={slideContent}
                now={now}
                hours={hours}
                minutes={minutes}
                toggleBottomSheet={toggleBottomSheet}
                slideRefs={slideRefs}
                hasSetInitialScroll={hasSetInitialScroll}
                setHasSetInitialScroll={setHasSetInitialScroll}
                activeCalendarBlock={activeCalendarBlock}
                setActiveCalendarBlock={setActiveCalendarBlock}
                onButtonClick={onButtonClick}
                activeLessonId={activeLessonId}
                setActiveLessonId={setActiveLessonId}
                eventsByStartDate={eventsByStartDate}
                isOwner={isOwner}
                isAnonymous={isAnonymous}
                showName={showName}
                coachAvailability={coachAvailability}
                onOverflowClick={onOverflowClick}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      ) : (
        <div className="bg-color-bg-lightmode-secondary px-6 pt-2">
          <WeeklyCalendarGrid>
            <div>&nbsp;</div>
            {new Array(DAYS_PER_WEEK).fill(null).map((day, i) => {
              return (
                <div
                  key={day?.toDateString() || i}
                  className="rounded border border-color-bg-lightmode-secondary  bg-color-bg-lightmode-inactive pb-2 pt-1.5 text-center text-xs"
                >
                  <div>&nbsp;</div>
                  <div>&nbsp;</div>
                </div>
              );
            })}
          </WeeklyCalendarGrid>
        </div>
      )}
    </div>
  );
};
export default React.memo(CalendarSwiper);
